import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { decodeAudioPeaksFromUrl } from "../lib/audio-peaks";

interface Progress {
  processed: number;
  total: number;
  currentName: string | null;
  succeeded: number;
  failed: number;
}

export default function BackgroundMusicPeaks() {
  const queryClient = useQueryClient();

  const [batchSize, setBatchSize] = useState(10);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);

  // Lightweight query just to show how many tracks still need peaks.
  const { data, isLoading, error } = useQuery({
    queryKey: ["adminPendingPeaksCount"],
    queryFn: () => AdminApi.getPendingBackgroundMusicPeaks(1),
    retry: false,
  });

  const remaining = data?.remaining ?? 0;

  const runBatch = async () => {
    if (isRunning) return;
    setIsRunning(true);

    try {
      const { tracks } =
        await AdminApi.getPendingBackgroundMusicPeaks(batchSize);

      if (tracks.length === 0) {
        toast.success("All background music tracks already have peaks");
        queryClient.invalidateQueries({ queryKey: ["adminPendingPeaksCount"] });
        return;
      }

      setProgress({
        processed: 0,
        total: tracks.length,
        currentName: null,
        succeeded: 0,
        failed: 0,
      });

      let succeeded = 0;
      let failed = 0;

      // Process sequentially — never in parallel — so decoding stays stable and
      // the browser isn't overwhelmed. Idempotent: only tracks missing peaks or
      // duration are returned, so re-running skips completed ones.
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]!;
        setProgress({
          processed: i,
          total: tracks.length,
          currentName: track.name,
          succeeded,
          failed,
        });

        try {
          const { peaks, durationMs } = await decodeAudioPeaksFromUrl(
            track.src,
          );
          // Only upload peaks when the track doesn't already have them.
          await AdminApi.saveBackgroundMusicPeaks(
            track.externalId,
            durationMs,
            track.hasPeaks ? undefined : peaks,
          );
          succeeded++;
        } catch (err) {
          failed++;
          console.error(
            `Failed to backfill peaks for "${track.name}" (${track.externalId})`,
            err,
          );
        }

        setProgress({
          processed: i + 1,
          total: tracks.length,
          currentName: null,
          succeeded,
          failed,
        });
      }

      if (failed === 0) {
        toast.success(
          `Backfilled peaks for ${succeeded} track${succeeded !== 1 ? "s" : ""}`,
        );
      } else {
        toast.warning(
          `Backfilled ${succeeded} track${succeeded !== 1 ? "s" : ""}, ${failed} failed`,
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to backfill peaks",
      );
    } finally {
      setIsRunning(false);
      queryClient.invalidateQueries({ queryKey: ["adminPendingPeaksCount"] });
    }
  };

  return (
    <AdminLayout
      title="Background Music Peaks"
      description="Backfill pre-decoded waveform peaks for background music tracks that are missing them"
      breadcrumb="Background Music Peaks"
    >
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="mb-6">
            <p className="text-sm text-gray-500 mb-1">Tracks missing peaks</p>
            {isLoading ? (
              <p className="text-3xl font-bold text-gray-400">…</p>
            ) : error ? (
              <p className="text-sm text-red-600">
                {error instanceof Error
                  ? error.message
                  : "Failed to load count"}
              </p>
            ) : (
              <p className="text-3xl font-bold text-gray-900">{remaining}</p>
            )}
          </div>

          <div className="flex items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch size
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={batchSize}
                disabled={isRunning}
                onChange={(e) =>
                  setBatchSize(
                    Math.min(100, Math.max(1, Number(e.target.value) || 1)),
                  )
                }
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <button
              onClick={runBatch}
              disabled={isRunning || remaining === 0}
              className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunning ? "Running…" : "Run backfill batch"}
            </button>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Peaks are decoded in your browser with WaveSurfer (identical to the
            upload path) and saved one track at a time. Run repeatedly until the
            count reaches zero.
          </p>
        </div>

        {progress && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">Progress</h3>
              <span className="text-sm text-gray-600">
                {progress.processed} / {progress.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{
                  width: `${progress.total > 0 ? (progress.processed / progress.total) * 100 : 0}%`,
                }}
              />
            </div>
            {progress.currentName && (
              <p className="text-sm text-gray-600 truncate">
                Decoding: {progress.currentName}
              </p>
            )}
            <div className="mt-2 flex gap-4 text-sm">
              <span className="text-green-700">
                Succeeded: {progress.succeeded}
              </span>
              {progress.failed > 0 && (
                <span className="text-red-700">Failed: {progress.failed}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
